import { coverColour, mostReadable } from '../color.js';

// The NES could show 54 colours and a background tile could use four of them.
// That constraint is most of what "8-bit" looks like, so this file is the one
// that enforces it: nothing in the level is drawn in a colour that is not in
// the table below.
//
// It matters most for the books. A shelf here holds several hundred spines,
// each carrying a hue, saturation and lightness measured off a real cover, and
// drawn straight those are several hundred slightly different mid-tones - a
// photograph of a bookshelf, not a game. Snapping each to the nearest of
// twelve families and one of four tones is what turns them back into sprites,
// and it is why two books of nearly the same colour come out identical here
// and distinguishable on the classic shelf. That is the trade, not a bug.

// Twelve hue families by four tones, darkest first, taken off the 2C02's
// output. Each family is one column of the hardware palette, which is why the
// hue drifts a little between a family's tones - the console's ramps are not
// constant-hue and imitating one that is looks wrong.
const FAMILIES = [
  { hue: 13, tones: ['#a81000', '#f83800', '#f87858', '#f8b8b8'] }, // red
  { hue: 29, tones: ['#881400', '#e45c10', '#fca044', '#f0d0b0'] }, // orange
  { hue: 44, tones: ['#503000', '#ac7c00', '#f8b800', '#f8d878'] }, // gold
  { hue: 73, tones: ['#007800', '#00b800', '#b8f818', '#d8f878'] }, // lime
  { hue: 118, tones: ['#006800', '#00a800', '#58d854', '#b8f8b8'] }, // green
  { hue: 145, tones: ['#005800', '#00a844', '#58f898', '#b8f8d8'] }, // mint
  { hue: 175, tones: ['#004058', '#008888', '#00e8d8', '#3cfcfc'] }, // cyan
  { hue: 199, tones: ['#0000a8', '#0058f8', '#3cbcfc', '#a4e4fc'] }, // sky
  { hue: 225, tones: ['#0000bc', '#0000fc', '#6888fc', '#b8b8f8'] }, // blue
  { hue: 255, tones: ['#4428bc', '#6844fc', '#9878f8', '#d8b8f8'] }, // violet
  { hue: 300, tones: ['#940084', '#d800cc', '#f878f8', '#f8b8f8'] }, // magenta
  { hue: 334, tones: ['#a80020', '#e40058', '#f85898', '#f8a4c0'] }, // rose
];

// The achromatic column, for covers with no colour in them at all. A greyscale
// cover really is grey, and pushing it into the nearest hue family would make
// every black-and-white photography book turn out red.
const GREYS = { hue: -1, tones: ['#1c1c1c', '#7c7c7c', '#bcbcbc', '#f8f8f8'] };

// Below this there is not enough colour to name, so the greys are the honest
// answer. Above it the hue is meaningful even at low saturation.
const ACHROMATIC_SATURATION = 12;

// Where each tone starts, in lightness. Uneven on purpose: the top tone is
// nearly white on this hardware and a shelf drawn mostly in it is unreadable,
// so it is reserved for covers that really are pale.
const TONE_FLOORS = [0, 30, 52, 76];

function familyFor(hue) {
  let best = FAMILIES[0];
  let bestDistance = Infinity;
  for (const family of FAMILIES) {
    // Circular, or a cover at hue 355 would file as a rose rather than as the
    // red it is a few degrees away from.
    const distance = Math.abs(((hue - family.hue + 540) % 360) - 180);
    if (distance < bestDistance) {
      best = family;
      bestDistance = distance;
    }
  }
  return best;
}

function toneFor(lightness) {
  let tone = 0;
  for (let i = 0; i < TONE_FLOORS.length; i++) {
    if (lightness >= TONE_FLOORS[i]) tone = i;
  }
  return tone;
}

// The two inks a sprite is ever lettered in - the same pair the console had.
export const INK_DARK = '#1c1c1c';
export const INK_LIGHT = '#f8f8f8';
const INK_DARK_RGB = [0x1c, 0x1c, 0x1c];
const INK_LIGHT_RGB = [0xf8, 0xf8, 0xf8];

const hexToRgb = (hex) => [
  parseInt(hex.slice(1, 3), 16),
  parseInt(hex.slice(3, 5), 16),
  parseInt(hex.slice(5, 7), 16),
];

// bookPalette is the four colours one book sprite is drawn from: the face of
// the spine, a lit edge, a shadowed edge, and whatever ink is readable on the
// face. Four, because that is a tile's worth.
//
// The lit and shadowed edges are the neighbouring tones of the same family
// rather than a computed lighten/darken, which is what keeps the whole level
// inside the table even where a book sits at the end of its ramp.
export function bookPalette(book) {
  const { hue, saturation, lightness } = coverColour(book);
  const family = saturation < ACHROMATIC_SATURATION ? GREYS : familyFor(hue);
  const tone = toneFor(lightness);

  const face = family.tones[tone];
  const light = family.tones[Math.min(3, tone + 1)];
  const dark = family.tones[Math.max(0, tone - 1)];

  // Measured, not thresholded, for the reason given in color.js: a tone-2 gold
  // and a tone-2 blue are nothing like as bright as each other.
  const { ink: inkRGB } = mostReadable(hexToRgb(face), [INK_DARK_RGB, INK_LIGHT_RGB]);
  const ink = inkRGB === INK_DARK_RGB ? INK_DARK : INK_LIGHT;

  return { face, light, dark, ink, family: family.hue, tone };
}

// Everything the level itself is built from, so a prop can never introduce a
// colour the console did not have. Named by what they are rather than by their
// index, since the index means nothing at the call site.
export const SCENE = {
  wallDark: '#0c0c3c', // not on the hardware chart; the NES's own "black" $0F
  wall: '#1c1c58',
  wallLit: '#4428bc',
  wallTrim: '#6844fc',
  floor: '#503000',
  floorDark: '#2c1c00',
  floorLit: '#ac7c00',
  wood: '#881400',
  woodLit: '#e45c10',
  woodDark: '#3c0800',
  board: '#a81000',
  brass: '#f8b800',
  brassDark: '#ac7c00',
  ink: INK_DARK,
  paper: '#f8f8f8',
  shadow: 'rgba(12, 12, 60, 0.55)',
};
